import { useState } from 'react'
import { cn } from '@/lib/utils'
import { Bitcoin, Send, Loader2, HelpCircle, X, CreditCard } from 'lucide-react'
import { Portal } from '@/components/ui/portal'
import { AnalyticsService } from '@/services/analytics'

interface SubscribeButtonProps {
  onCryptoPurchase: () => void
  onTelegramPurchase: () => void
  onCardPurchase?: () => void
  disabled?: boolean
  isCurrentPlan?: boolean
  isDowngrade?: boolean
  cryptoLoading?: boolean
  telegramLoading?: boolean
  className?: string
}

export function SubscribeButton({
  onCryptoPurchase,
  onTelegramPurchase,
  onCardPurchase,
  disabled = false,
  isCurrentPlan = false,
  isDowngrade = false,
  cryptoLoading = false,
  telegramLoading = false,
  className,
}: SubscribeButtonProps) {
  const [showTgInfo, setShowTgInfo] = useState(false)

  if (isCurrentPlan) {
    return (
      <div className={cn('w-full', className)}>
        <button
          disabled
          className="w-full py-3 rounded-[10px] text-sm font-medium bg-white/[0.05] text-[#666] cursor-not-allowed"
        >
          Current Plan
        </button>
      </div>
    )
  }

  if (isDowngrade) {
    return (
      <div className={cn('w-full', className)}>
        <button
          disabled
          className="w-full py-3 rounded-[10px] text-sm font-medium bg-white/[0.03] text-[#555] cursor-not-allowed"
        >
          Included in your plan
        </button>
        <p className="mt-2 text-center text-xs text-[#555]">
          You're on a higher plan
        </p>
      </div>
    )
  }

  const isLoading = cryptoLoading || telegramLoading

  return (
    <>
      <div className={cn('flex flex-col gap-2 w-full', className)}>
        {/* Card payment (primary) */}
        {onCardPurchase && (
          <button
            onClick={onCardPurchase}
            disabled={disabled || isLoading}
            className="w-full py-3 rounded-[10px] text-sm font-semibold transition-all flex items-center justify-center gap-2 bg-violet-500 text-white hover:bg-violet-400 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <CreditCard className="size-4" />
            Pay with Card
          </button>
        )}

        {/* How TG payments work */}
        <button
          onClick={() => {
            setShowTgInfo(true)
            AnalyticsService.track('TG Payment Info Clicked', {})
          }}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-[10px] text-xs font-medium text-[#888] hover:text-white bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.06] hover:border-white/[0.1] transition-all"
        >
          <HelpCircle className="size-3.5" />
          How do Telegram payments work?
        </button>

        {/* Telegram payment */}
        <button
          onClick={onTelegramPurchase}
          disabled={disabled || isLoading}
          className="w-full py-3 rounded-[10px] text-sm font-medium transition-all flex items-center justify-center gap-2 bg-sky-500/20 text-[#e5e5e5] hover:bg-sky-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {telegramLoading ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Send className="size-4" />
          )}
          Pay with Card via Telegram
        </button>

        {/* Crypto payment */}
        <button
          onClick={onCryptoPurchase}
          disabled={disabled || isLoading}
          className="w-full py-3 rounded-[10px] text-sm font-medium transition-all flex items-center justify-center gap-2 bg-violet-500/20 text-[#e5e5e5] hover:bg-violet-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {cryptoLoading ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Bitcoin className="size-4" />
          )}
          Pay with Crypto
        </button>
      </div>

      {showTgInfo && <TelegramInfoModal onClose={() => setShowTgInfo(false)} />}
    </>
  )
}

function TelegramInfoModal({ onClose }: { onClose: () => void }) {
  return (
    <Portal>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
        <div className="relative w-full max-w-md rounded-2xl bg-[#111] border border-white/[0.1] p-6 shadow-2xl">
          {/* Close */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1 rounded-lg text-[#666] hover:text-white hover:bg-white/[0.05] transition-colors"
          >
            <X className="size-5" />
          </button>

          {/* Icon */}
          <div className="flex justify-center mb-5">
            <div className="p-3 rounded-full bg-sky-500/10 border border-sky-500/20">
              <Send className="size-7 text-sky-400" />
            </div>
          </div>

          <h2 className="text-lg font-semibold text-white text-center mb-4">
            How Telegram Payments Work
          </h2>

          <div className="space-y-4 text-sm text-[#999]">
            <div className="flex gap-3">
              <span className="shrink-0 flex items-center justify-center size-6 rounded-full bg-sky-500/10 text-sky-400 text-xs font-bold">1</span>
              <p>Click "Pay with Card via Telegram" and you'll be redirected to our Telegram bot <strong className="text-white">@PixelmakerBot</strong>.</p>
            </div>
            <div className="flex gap-3">
              <span className="shrink-0 flex items-center justify-center size-6 rounded-full bg-sky-500/10 text-sky-400 text-xs font-bold">2</span>
              <p>Telegram will open a secure payment form where you can pay with your <strong className="text-white">credit or debit card</strong> — Visa, Mastercard, and more are accepted.</p>
            </div>
            <div className="flex gap-3">
              <span className="shrink-0 flex items-center justify-center size-6 rounded-full bg-sky-500/10 text-sky-400 text-xs font-bold">3</span>
              <p>Once payment is confirmed, your credits will be <strong className="text-white">added to your account instantly</strong> and you can start generating right away.</p>
            </div>
          </div>

          <div className="mt-5 p-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
            <p className="text-xs text-[#666] leading-relaxed">
              Payments are processed securely through Telegram's built-in payment system. Your card details are handled by Telegram's payment provider and are never shared with us.
            </p>
          </div>

          <button
            onClick={onClose}
            className="w-full mt-5 py-3 rounded-xl bg-sky-500/20 text-white text-sm font-medium hover:bg-sky-500/30 transition-colors"
          >
            Got it
          </button>
        </div>
      </div>
    </Portal>
  )
}
